import api_base from '@/api/base';

/* =========================
   Types
========================= */

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  domain_name?: string;

  // 🔹 Markdown content (BACKEND FIELD)
  content_markdown: string;

  summary?: string | null;

  is_published: boolean;
  published_at?: string | null;

  created_at: string;
  updated_at: string;

  category?: {
    id: string;
    name: string;
    slug: string;
  } | null;

  hashtags?: {
    id: string;
    name: string;
    slug: string;
  }[];
  language?: string;
  cover_image?: string | null;
}

export interface BlogPostWrite {
  title?: string;
  summary?: string | null;
  content_markdown?: string;
  domain_name?: string; // 👈 NEW
  category_id?: string | null; // 👈 NEW
  hashtag_ids?: string[]; //              // 👈 NEW
  cover_image?: string | null;
  is_published?: boolean;
  language?: string;
  metadata?: Record<string, any>;
}

/* =========================
   PUBLIC (Frontend)
   Django:
   - PublicBlogPostListView
   - PublicBlogPostDetailView
========================= */

/**
 * Get all published blog posts
 * GET /api/v1/blog/posts/
 */
export const get_public_blog_posts = async (domain_name?: string): Promise<BlogPost[]> => {
  const response = await api_base.get('/api/v1/blog/posts/', {
    headers: domain_name ? { 'X-Domain-Name': domain_name } : undefined
  });
  return response.data.results ?? response.data;
};

/**
 * Get published blog post by slug
 * GET /api/v1/blog/posts/<slug>/
 */
export const get_public_blog_post = async (
  id: string
): Promise<BlogPost> => {
  const response = await api_base.get(
    `/api/v1/blog/posts/${id}/`
  );
  return response.data;
};

/* =========================
   SUPER ADMIN (Backoffice)
   Django:
   - SuperAdminBlogPostListCreateView
   - SuperAdminBlogPostDetailView
========================= */

/**
 * List all blog posts (draft + published)
 * GET /api/v1/blog/super-admin/posts/
 */
export const get_superadmin_blog_posts = async (domain_name?: string): Promise<BlogPost[]> => {
  const response = await api_base.get(
    '/api/v1/blog/super-admin/posts/',
    {
        headers: domain_name ? { 'X-Domain-Name': domain_name } : undefined
    }
  );
  return response.data.results ?? response.data;
};

/**
 * Create blog post
 * POST /api/v1/blog/super-admin/posts/
 */
export const create_superadmin_blog_post = async (
  data: BlogPostWrite
): Promise<BlogPost> => {
  const response = await api_base.post(
    '/api/v1/blog/super-admin/posts/',
    data
  );
  return response.data;
};

/**
 * Get blog post by UUID (admin)
 * GET /api/v1/blog/super-admin/posts/<uuid>/
 */
export const get_superadmin_blog_post = async (
  id: string
): Promise<BlogPost> => {
  const response = await api_base.get(
    `/api/v1/blog/super-admin/posts/${id}/`
  );
  return response.data;
};

/**
 * Update blog post (PATCH)
 * PATCH /api/v1/blog/super-admin/posts/<uuid>/
 */
export const update_superadmin_blog_post = async (
  id: string,
  data: BlogPostWrite
): Promise<BlogPost> => {
  const response = await api_base.patch(
    `/api/v1/blog/super-admin/posts/${id}/`,
    data
  );
  return response.data;
};

/**
 * Delete blog post
 * DELETE /api/v1/blog/super-admin/posts/<uuid>/
 */
export const delete_superadmin_blog_post = async (
  id: string
): Promise<void> => {
  await api_base.delete(
    `/api/v1/blog/super-admin/posts/${id}/`
  );
};

/**
 * Publish blog post (superadmin)
 * POST /api/v1/blog/super-admin/posts/<uuid>/publish/
 */
export const publish_superadmin_blog_post = async (
  id: string
): Promise<BlogPost> => {
  const response = await api_base.post(
    `/api/v1/blog/super-admin/posts/${id}/publish/`
  );
  return response.data;
};

/* =========================
   DOMAINS (Super Admin)
========================= */

export interface BlogDomain {
  id: string;
  name: string;
  project_id?: string | null;
  created_at: string;
  updated_at: string;
}

export const list_blog_domains = async (): Promise<BlogDomain[]> => {
  const res = await api_base.get('/api/v1/blog/super-admin/domains/');
  return res.data.results ?? res.data;
};

export const create_blog_domain = async (name: string, project_id?: string): Promise<BlogDomain> => {
  const payload: any = { name };
  if (project_id) payload.project_id = project_id;
  const res = await api_base.post('/api/v1/blog/super-admin/domains/', payload);
  return res.data;
};

export const update_blog_domain = async (id: string, name: string, project_id?: string): Promise<BlogDomain> => {
  const payload: any = { name };
  if (project_id !== undefined) payload.project_id = project_id;
  const res = await api_base.patch(`/api/v1/blog/super-admin/domains/${id}/`, payload);
  return res.data;
};

export const delete_blog_domain = async (id: string): Promise<void> => {
  await api_base.delete(`/api/v1/blog/super-admin/domains/${id}/`);
};

export interface BlogCategory {
  id: string;
  name: string;
  slug?: string;
}

// List categories - domain filtering using X-Domain-Name header
export const list_blog_categories = async (domain_name?: string): Promise<BlogCategory[]> => {
  const res = await api_base.get('/api/v1/blog/categories/', {
    headers: domain_name ? { 'X-Domain-Name': domain_name } : undefined
  });
  return res.data.results ?? res.data;
};

// Create category
export const create_blog_category = async (name: string, domain_name?: string): Promise<BlogCategory> => {
  const res = await api_base.post('/api/v1/blog/categories/', { name, domain_name });
  return res.data;
};

export const updateBlogCategory = async (id: string, name: string): Promise<BlogCategory> => {
  const res = await api_base.put(`/api/v1/blog/categories/${id}/`, { name });
  return res.data;
};

export const deleteBlogCategory = async (id: string) => {
  await api_base.delete(`/api/v1/blog/categories/${id}/`);
};

export interface BlogTag {
  id: string;
  name: string;
  slug?: string;
}

// List tags
export const list_blog_tags = async (domain_name?: string): Promise<BlogTag[]> => {
  const res = await api_base.get('/api/v1/blog/tags/', {
    headers: domain_name ? { 'X-Domain-Name': domain_name } : undefined
  });
  return res.data.results ?? res.data;
};

// Create tag
export const create_blog_tag = async (name: string, domain_name?: string): Promise<BlogTag> => {
  const res = await api_base.post('/api/v1/blog/tags/', { name, domain_name });
  return res.data;
};

// Update tag
export const updateBlogTag = async (id: string, name: string): Promise<BlogTag> => {
  const res = await api_base.put(`/api/v1/blog/tags/${id}/`, { name });
  return res.data;
};

// Delete tag
export const deleteBlogTag = async (id: string) => {
  await api_base.delete(`/api/v1/blog/tags/${id}/`);
};

/**
 * Save a blog preview (upload cover image + other data)
 * POST /api/v1/blog/super-admin/blog/preview/
 */
export interface BlogPreviewWrite {
  title?: string;
  summary?: string | null;
  content_markdown?: string;
  category_id?: string | null;
  hashtag_ids?: string[];
  cover_image?: string | null;
  language?: string;
  domain_name?: string; // 👈 NEW
}

export const create_superadmin_blog_preview = async (
  data: BlogPreviewWrite
): Promise<BlogPost> => {
  const formData = new FormData();

  if (data.title) formData.append('title', data.title);
  if (data.summary) formData.append('summary', data.summary);
  if (data.content_markdown) formData.append('content_markdown', data.content_markdown);
  if (data.category_id) formData.append('category_id', data.category_id);
  if (data.hashtag_ids)   data.hashtag_ids.forEach((id) => formData.append('hashtag_ids', id));
  if (data.cover_image) formData.append('cover_image', data.cover_image);
  if (data.language) formData.append('language', data.language);
  if (data.domain_name) formData.append('domain_name', data.domain_name);

  const res = await api_base.post('/api/v1/blog/super-admin/blog/preview/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return res.data;
};

/**
 * Unpublish blog post (superadmin)
 * POST /api/v1/blog/super-admin/posts/<uuid>/unpublish/
 */
export const unpublish_superadmin_blog_post = async (
  id: string
): Promise<BlogPost> => {
  const response = await api_base.post(
    `/api/v1/blog/super-admin/posts/${id}/unpublish/`
  );
  return response.data;
};

export const upload_blog_cover = async (
  blogId: string,
  file: File
): Promise<{ cover_image: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api_base.post(
    `/api/v1/blog/super-admin/posts/${blogId}/upload-cover/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );

  return response.data;
};
